<script lang="ts">
  import { onMount } from "svelte";
  import { navigate } from "../../app.logic";
  import Input from "../../components/Form/Fields/Input.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import type { IClassroom } from "../../entities/classroom";
  import FormLayout from "../../layout/FormLayout.svelte";
  import MainLayout from "../../layout/MainLayout.svelte";
  import type { IApiResponse } from "../../utils/api/api-response";
  import {
    initDetail,
    record,
    save,
    loading,
    participants,
    onAddParticipant,
    onDeleteParticipant,
    selectedParticipantId,
    deleteWholeClassroom,
    onClassroomExport,
    onClassroomUpload,
    importDiagnostics,
  } from "./classroom.store";
  import Table from "../../components/Table/Table.svelte";
  import { classroomParticipantDtoFields } from "../../utils/TableFields/classroom-participant-dto.fields";
  import PortalUserWithoutClassroomDropdown from "../../components/Business/PortalUserWithoutClassroomDropdown.svelte";
  import Button from "../../components/Button.svelte";
  import ClfDropdown from "../../components/Form/Fields/Dropdown/ClfDropdown.svelte";
  import ConfirmModal from "../../components/Modal/ConfirmModal.svelte";
  import FileUploadButton from "../../components/FileUploadButton.svelte";
  import Icon from "../../components/Icon.svelte";
  import RawFileUploadButton from "../../components/RawFileUploadButton.svelte";

  export let oid: number;

  let deleteModalOpen = false;

  $: isNew = oid == 0;
  $: canSubmit = $record != null && $record.code != "" && $record.name != "";

  async function onSubmit() {
    let newP = isNew;
    let _resp: IApiResponse<IClassroom> = await save();
    let u = _resp.payload;
    if (newP) {
      oid = u.oid;
      record.set(u);
      navigate(`/classroom/${u.oid}`);
    } else {
      onCancel();
    }
  }

  function onCancel() {
    navigate(`/classroom`);
  }

  function onDeleteWholeClassroom() {
    deleteModalOpen = true;
  }

  function confirmDeleteWholeClassroom() {
    deleteModalOpen = false;
    deleteWholeClassroom();
  }

  function declineDeleteWholeClassroom() {
    deleteModalOpen = false;
  }

  onMount(() => initDetail(oid));
</script>

<MainLayout>
  <ConfirmModal
    title="Deletion"
    message="Really delete whole classroom and its members?"
    bind:open={deleteModalOpen}
    on:confirm={confirmDeleteWholeClassroom}
    on:decline={declineDeleteWholeClassroom}
  />
  {#if $record}
    <div class="columns">
      <div class="column is-6">
        <section class="section">
          <h2 class="title">Classroom data</h2>
          <FormLayout {canSubmit} {isNew} on:submit={onSubmit} on:cancel={onCancel}>
            <FormField>
              <Input name="Code" placeholder="code" required={true} bind:value={$record.code} />
            </FormField>
            <FormField>
              <Input name="Name" placeholder="name" required={true} bind:value={$record.name} />
            </FormField>
            <FormField>
              <ClfDropdown name="Default Compiler" schema="COMPLRTYPE" required={false} clearVisible={true} bind:value={$record.clfCompilerId} />
            </FormField>
          </FormLayout>
        </section>

        {#if $importDiagnostics && $importDiagnostics.length > 0}
          <section class="section box">
            <h2 class="subtitle">Import results</h2>
            {#each $importDiagnostics as msg}
              <p>{msg}</p>
            {/each}
            <br />
            <Button klass="is-primary" on:click={(_) => ($importDiagnostics = null)}>Dismiss</Button>
          </section>
        {/if}
      </div>

      <div class="column is-6">
        {#if oid > 0}
          <section class="section">
            <h2 class="title">Classroom Members</h2>

            <div class="columns">
              <div class="column is-8">
                <PortalUserWithoutClassroomDropdown bind:value={$selectedParticipantId} />
              </div>
              <div class="column is-4">
                <Button disabled={$selectedParticipantId == 0} on:click={() => onAddParticipant(oid, $selectedParticipantId)}>Add</Button>
              </div>
            </div>

            <Table
              loading={$loading}
              fields={classroomParticipantDtoFields}
              records={$participants}
              pagination={null}
              createEnabled={false}
              editEnabled={false}
              deleteEnabled={true}
              on:delete={(e) => onDeleteParticipant(e.detail)}
            />

            <RawFileUploadButton klass="is-primary" text="Import members" accept=".xlsx" on:fileUpload={onClassroomUpload} />
            <Button klass="is-primary" on:click={onClassroomExport}>
              <Icon name="download" handleClick={false} />
              <span>Export members</span>
            </Button>
            <Button klass="is-danger" on:click={onDeleteWholeClassroom}>
              <Icon name="delete" klass="dangerous" handleClick={false} />
              <span>Delete classroom and members</span>
            </Button>
          </section>
        {/if}
      </div>
    </div>
  {/if}
</MainLayout>
